'use client'

import { useEffect, useRef } from 'react'
import Link from 'next/link'
import { ArrowRight, FileText } from 'lucide-react'
import { trackBridgeCtaView, trackBridgeCtaClick } from '@/lib/analytics'

/**
 * Reusable "next step" bridge that routes traffic by intent temperature
 * (Master Plan 4.2). Fires bridge_cta_view once when it scrolls into view and
 * bridge_cta_click (target = destination href) on either button.
 *
 * - cold  → primary "Check your next script free", secondary "See a sample
 *           audit". For informational pages (guides, word-count tools).
 * - hot   → primary "See pricing" (?ref=<page>), secondary "Check your next
 *           script free". For high-intent pages (what-is-script-analysis,
 *           niches, and post-result upsells on the free tools).
 *
 * The scanner was the default target until 2026-07-19; it was demoted after a
 * week of funnel data showed 78% of bridge impressions pointing at a tool that
 * failed most attempts (caption restrictions). Default target is now the free
 * script check, the top of the working free-check funnel. The ?source= param tags the
 * originating surface for funnel segmentation.
 *
 * Client component, but safe to drop into server-rendered (static) parents.
 */

const SAMPLE_HREF = '/example-analysis'
const checkHref = (page: string) => `/upload?source=bridge-${encodeURIComponent(page)}`

interface MonetizationBridgeCTAProps {
  /** Analytics surface identifier, e.g. "guide-first-30-seconds". */
  page: string
  temperature: 'cold' | 'hot'
  /** Visual density hint; both render the same restrained banner. */
  position?: 'inline' | 'footer'
}

export function MonetizationBridgeCTA({
  page,
  temperature,
  position = 'inline',
}: MonetizationBridgeCTAProps) {
  const ref = useRef<HTMLDivElement>(null)
  const viewedRef = useRef(false)

  const primary =
    temperature === 'hot'
      ? { label: 'See pricing', href: `/pricing?ref=${encodeURIComponent(page)}`, icon: ArrowRight }
      : { label: 'Check your next script free', href: checkHref(page), icon: FileText }
  const secondary =
    temperature === 'hot'
      ? { label: 'Check your next script free', href: checkHref(page) }
      : { label: 'See a sample audit', href: SAMPLE_HREF }

  const heading =
    temperature === 'hot'
      ? 'Ready to check your next script?'
      : 'See where your next script leaks viewers'
  const sub =
    temperature === 'hot'
      ? 'See where your script is most likely to lose attention, and what to change, before you record.'
      : 'Paste your script, get your scores and the biggest leak for free. No login.'

  // Fire the impression once, when the banner first enters the viewport.
  useEffect(() => {
    const el = ref.current
    const fire = () => {
      if (viewedRef.current) return
      viewedRef.current = true
      trackBridgeCtaView({ page, target: primary.href })
    }
    if (!el || typeof IntersectionObserver === 'undefined') {
      fire()
      return
    }
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            fire()
            observer.disconnect()
            break
          }
        }
      },
      { threshold: 0.4 }
    )
    observer.observe(el)
    return () => observer.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, primary.href])

  const PrimaryIcon = primary.icon

  return (
    <div
      ref={ref}
      className={`not-prose rounded-xl border border-primary/20 bg-primary/5 p-6 text-center sm:p-8 ${
        position === 'footer' ? 'sm:text-left' : ''
      }`}
    >
      <h3 className="font-heading text-xl font-bold text-foreground mb-2">{heading}</h3>
      <p className="text-muted-foreground mb-5 max-w-xl mx-auto sm:mx-0">{sub}</p>
      <div className={`flex flex-col sm:flex-row gap-3 ${position === 'footer' ? '' : 'justify-center'}`}>
        <Link
          href={primary.href}
          onClick={() => trackBridgeCtaClick({ page, target: primary.href })}
          className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-6 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          <PrimaryIcon className="h-4 w-4" />
          {primary.label}
        </Link>
        <Link
          href={secondary.href}
          onClick={() => trackBridgeCtaClick({ page, target: secondary.href })}
          className="inline-flex items-center justify-center gap-2 rounded-md border border-border px-6 py-3 text-sm font-medium text-foreground hover:bg-secondary transition-colors"
        >
          {temperature === 'cold' && <FileText className="h-4 w-4" />}
          {secondary.label}
        </Link>
      </div>
    </div>
  )
}
